import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Widgets from '../components/WidgetTiles';
import { motion } from 'framer-motion';
import TeamButton from '../components/team/TeamButton';
import StaffButton from '../components/team/StaffButton';
import Biographies from '../components/team/Biographies';

const Team = () => {
  const [activeTab, setActiveTab] = useState("providers");
  const [activeButton, setActiveButton] = useState('');
  const [providers, setProviders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('none selected');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const myRef = useRef(null);
  
  const setURL = (providerName) => {
    setSelectedProvider(providerName);
    if (window.history.pushState) {
      const newurl = window.location.protocol + "//" + window.location.host + 
                    window.location.pathname + '?provider=' + encodeURIComponent(providerName);
      window.history.pushState({ path: newurl }, '', newurl);
    }
  };

  const executeScroll = () => myRef.current.scrollIntoView({ behavior: 'smooth' });

  const getUrlParameter = (name) => {
    //eslint-disable-next-line
    name = name.replace(/\[/, '[').replace(/\]/, ']');
    const regex = new RegExp('[?&]' + name + '=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  useEffect(() => {
    // Check URL parameter on load
    const providerParam = getUrlParameter('provider');
    if (providerParam) {
      setSelectedProvider(providerParam);
    }    const fetchProviders = async (retryCount = 0) => {
      const maxRetries = 3;
      const urls = [
        'https://rpsych.sfo3.digitaloceanspaces.com/ProviderList.json',
        // Fallback URLs if needed
        '/api/providers', // If you have a local API endpoint
      ];      for (const url of urls) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const response = await fetch(url, {
            signal: controller.signal,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          // Create a new map to group providers by ID
          const providersMap = new Map();
          data.forEach((provider) => {
            // Ignore virtual offices
            if (provider.OfficeName === 'Virtual') return;

            // Check if the provider is already in the map
            if (!providersMap.has(provider.ID_Provider)) {
              // If not, add them with an array containing their first office
              providersMap.set(provider.ID_Provider, {
                ...provider,
                Offices: [provider.OfficeName]
              });
            } else {
              // If so, add the new office to their Offices array
              const existingProvider = providersMap.get(provider.ID_Provider);
              existingProvider.Offices.push(provider.OfficeName);
            }
          });

          // Convert the map values to an array and sort it
          const sortedProviders = Array.from(providersMap.values())
            .sort(alphabetically(true));          // Update the providers state with the sorted providers
          setProviders(sortedProviders);
          return; // Success, exit the function
          
        } catch (error) {
          console.error(`Error fetching providers from ${url}:`, error.message);
            // If this was the last URL and we haven't exceeded retry count
          if (url === urls[urls.length - 1] && retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            return fetchProviders(retryCount + 1);
          }
        }
      }
      
      // If all attempts failed, set empty array and show user-friendly message
      console.error("All attempts to fetch providers failed. Using empty array.");
      setProviders([]);
    };
      const fetchStaff = async (retryCount = 0) => {
      const maxRetries = 3;
      const urls = [
        'https://rpsych.sfo3.digitaloceanspaces.com/StaffList.json',
        // Fallback URLs if needed
        '/api/staff', // If you have a local API endpoint
      ];      for (const url of urls) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const response = await fetch(url, {
            signal: controller.signal,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          // Define the desired order
          const customOrder = ["Dan Friday, CEO", "Heather Morin, COO", "Andrew Isabella, DoN", "Garrett Smith, DoMR"];

          // Sort the staff according to the custom order
          const sortedStaff = data.sort((a, b) => {
            const orderA = customOrder.indexOf(a.DisplayName);
            const orderB = customOrder.indexOf(b.DisplayName);
            return orderA - orderB;
          });
            setStaff(sortedStaff);
          return; // Success, exit the function
          
        } catch (error) {
          console.error(`Error fetching staff from ${url}:`, error.message);
            // If this was the last URL and we haven't exceeded retry count
          if (url === urls[urls.length - 1] && retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            return fetchStaff(retryCount + 1);
          }
        }
      }
      
      // If all attempts failed, set empty array and show user-friendly message
      console.error("All attempts to fetch staff failed. Using empty array.");
      setStaff([]);
    };    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([fetchProviders(), fetchStaff()]);
      } catch (err) {
        setError('Unable to load team data. Please try refreshing the page.');
        console.error('Error loading team data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  function alphabetically(ascending) {
    return function (a, b) {
      // equal items sort equally
      if (a.ListOrder === b.ListOrder) {
        return 0;
      }
      // nulls sort after anything else
      else if (a.ListOrder === null) {
        return 1;
      }
      else if (b.ListOrder === null) {
        return -1;
      }
      // otherwise, if we're ascending, lowest sorts first
      else if (ascending) {
        return a.ListOrder < b.ListOrder ? -1 : 1;
      }
      // if descending, highest sorts first
      else {
        return a.ListOrder < b.ListOrder ? 1 : -1;
      }
    };
  }

  return (
    <>
      <Helmet>
        <title>Our Team | Redemption Psychiatry</title>
      </Helmet>
      <div className="wrap">
        <Header />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div id="content" role="main" className="home">
            {/* Interior Header */}
            <div id="interior-header-container" className="container-fluid">
              <div className="container">
                <h1>{activeTab === "providers" ? "Provider Biographies" : "Admin & Outreach"}</h1>
              </div>
            </div>

            {/* Submenu */}
            <div id="interior-sub-menu-container" className="container-fluid">
              <div className="container">
                <ul>
                  <li><a href="#team" onClick={() => setActiveTab("providers")}>Providers</a></li>
                  <li><a href="#team" onClick={() => setActiveTab("staff")}>Staff Members</a></li>
                </ul>
              </div>
            </div>            {/* Team Section */}
            <div id="team" className="container my-5">
              <div className="p-4 rounded shadow bg-white">
                <div ref={myRef}></div>
                
                {/* Error Display */}
                {error && (
                  <div className="alert alert-warning" role="alert">
                    <strong>Connection Issue:</strong> {error}
                    <br />
                    <small>If this persists, please contact support or try a different network connection.</small>
                  </div>
                )}

                {/* Loading Display */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading team information...</span>
                    </div>
                    <p className="mt-3">Loading team information...</p>
                  </div>
                ) : (
                  <>
                    {activeTab === "providers" ? (
                      <div>
                        <div>
                          <Biographies
                            currentProvider={providers.filter(function (provider) {
                              return provider.DisplayName === selectedProvider
                            })}
                          />
                          <br />
                        </div>
                        <div className="row justify-content-center">
                          {providers.length === 0 ? (
                            <div className="col-12 text-center py-4">
                              <p className="text-muted">No providers available at this time.</p>
                            </div>
                          ) : (
                            providers.map((item, index) => (
                              <TeamButton
                                key={index}
                                id={index}
                                DisplayName={item.DisplayName}
                                FirstName={item.FirstName}
                                LastName={item.LastName}
                                PictureLink={item.PictureLink}
                                CurrentlyAcceptingPatients={item.CurrentlyAcceptingPatients}
                                setActiveButton={setActiveButton}
                                executeScroll={executeScroll}
                                setURL={setURL}
                                Offices={item.Offices}
                                url={selectedProvider}
                                active={activeButton === index ? true : false}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    ) : (
                      // Staff tab
                      <div className="row justify-content-center mt-4">
                        {staff.length === 0 ? (
                          <div className="col-12 text-center py-4">
                            <p className="text-muted">No staff information available at this time.</p>
                          </div>
                        ) : (
                          staff.map((item) => (
                            <StaffButton
                              key={item.ID_Staff}
                              DisplayName={item.DisplayName}
                              Title={item.Title}
                              PictureLink={item.PictureLink}
                              Email={item.Email}
                              Phone={item.Phone}
                            />
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Widgets */}
            <Widgets />
          </div>
          <div className="push"></div>
        </motion.div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Team;